import { useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Full-screen image lightbox with prev/next + ESC to close.
 * items: [{ id, title, city, category, image }]
 * activeIndex: number (or null to hide)
 * onClose, onPrev, onNext
 */
const Lightbox = ({ items = [], activeIndex, onClose, onPrev, onNext }) => {
    const open = activeIndex !== null && activeIndex >= 0 && activeIndex < items.length;
    const item = open ? items[activeIndex] : null;

    const handleKey = useCallback(
        (e) => {
            if (!open) return;
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        },
        [open, onClose, onPrev, onNext],
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [handleKey]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-dark/95 backdrop-blur-sm animate-fade-in-up"
            role="dialog"
            aria-modal="true"
            aria-label={item.title}
            data-testid="lightbox"
        >
            {/* Click backdrop to close */}
            <button
                type="button"
                aria-label="Close lightbox"
                onClick={onClose}
                className="absolute inset-0 cursor-zoom-out"
                data-testid="lightbox-backdrop"
            />

            {/* Close */}
            <button
                type="button"
                onClick={onClose}
                className="absolute top-5 right-5 z-10 flex h-11 w-11 items-center justify-center bg-brand-dark text-white hover:bg-brand-coral transition-colors"
                aria-label="Close"
                data-testid="lightbox-close"
            >
                <X className="h-5 w-5" strokeWidth={2.25} />
            </button>

            {/* Prev */}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onPrev();
                }}
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 flex h-12 w-12 items-center justify-center bg-brand-dark/70 text-white hover:bg-brand-cyan transition-colors"
                aria-label="Previous image"
                data-testid="lightbox-prev"
            >
                <ChevronLeft className="h-6 w-6" strokeWidth={2.25} />
            </button>

            {/* Next */}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                }}
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 flex h-12 w-12 items-center justify-center bg-brand-dark/70 text-white hover:bg-brand-cyan transition-colors"
                aria-label="Next image"
                data-testid="lightbox-next"
            >
                <ChevronRight className="h-6 w-6" strokeWidth={2.25} />
            </button>

            {/* Image + caption */}
            <div
                className={cn(
                    "relative max-w-6xl w-[92%] max-h-[88vh] flex flex-col",
                )}
                onClick={onClose}
            >
                <div
                    className="relative bg-brand-dark border border-white/10 flex-1 min-h-0 flex items-center justify-center"
                    onClick={onClose}
                >
                    <img
                        src={item.image}
                        alt={`${item.title} in ${item.city}`}
                        className="max-h-[72vh] w-auto max-w-full object-contain"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="lightbox-image"
                    />
                </div>
                <figcaption
                    className="bg-brand-dark border border-t-0 border-white/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div>
                        <span className="inline-block bg-brand-coral text-white text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 mb-2">
                            {item.category}
                        </span>
                        <h3 className="font-display text-xl sm:text-2xl tracking-wide text-white leading-tight">
                            {item.title}
                        </h3>
                    </div>
                    <p className="text-xs text-white/65 flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-brand-cyan" strokeWidth={2.5} />
                        {item.city}
                    </p>
                    <p className="text-xs text-white/50 hidden sm:block">
                        {activeIndex + 1} / {items.length}
                    </p>
                </figcaption>
            </div>
        </div>
    );
};

export default Lightbox;
